import type { Middleware, MiddlewareAPI } from '@reduxjs/toolkit';
import type { OrderListParams } from '@features/orders/types';
import { tokenStore } from '@lib/tokenStore';
import { refreshAccessToken } from '@lib/baseQuery';
import { services } from '@config/services';
import { ordersApi } from '@features/orders/api';
import { markOrderProcessingCompleted, markOrderProcessingStarted } from '@features/orders/itemChangeRun';
import { shipmentsApi } from '@features/shipments/api';
import { historyApi } from '@features/history/api';
import { dispatchCacheAction } from '@lib/rtkQueryDispatch';
import { SseClient, type SseMessage } from './sseClient';
import { SseTabCoordinator, type SseCoordMessage } from './tabCoordinator';
import type { SseState } from './slice';
import {
  sseAuthError,
  sseConnected,
  sseDisconnected,
  sseEventReceived,
  sseReconnect,
  sseReconnecting,
  sseRetriesExhausted,
  sseStart,
  sseStop,
} from './slice';

/**
 * Middleware that owns the live SSE connection.
 * - `sseStart` opens the connection (lazy `tokenStore` accessor, with a
 * proactive refresh when the token is past its expiry — fetch hooks
 * bypass the RTK Query baseQuery so the expired-token path is on us).
 * - Each parsed envelope is dispatched as `sseEventReceived` (slice keeps
 * a bounded log) AND fans out to cache invalidations per `EventType`:
 * orderUpdate → patch order in `ordersApi` cache (status update)
 * orderProcessingStarted → patch order with processingLock, re-read an open order page, and count a saved item change's run
 * orderProcessingCompleted → patch order, clear processingLock, and end a saved item change's wait
 * - `sseStop` disconnects and resets the slice.
 * - On auth errors the slice's `authError` is set; the connection is left
 * closed until `sseAuthErrorCleared` (e.g. after a fresh login).
 * One connection per BROWSER: tabs elect a leader that owns the transport
 * and mirrors frames/status to follower tabs — see `tabCoordinator.ts`.
 * Where coordination is unsupported this degrades to one connection per tab.
 */

let client: SseClient | null = null;
/** Survives a stop/start cycle so the reconnecting client can send Last-Event-ID. */
let preservedLastEventId: string | null = null;
/** Cross-tab election + broadcast: only the leader tab owns `client`; every
 * other tab mirrors the leader's frames and status. See tabCoordinator.ts. */
let coordinator: SseTabCoordinator | null = null;
/** Latest verbose preference — the leader may connect after a later sseStart
 * updated it, so it lives outside the action handler. */
let currentVerbose = false;

declare global {
  interface Window {
    /** QA convenience: a bounded ring of received SSE frames, populated only
     * while verbose SSE logging is on (legacy `__SSE_FRAMES` parity). */
    __SSE_FRAMES?: SseMessage[];
  }
}

/** Cap the debug ring so a long-lived verbose session can't grow unbounded. */
const SSE_FRAMES_DEBUG_CAP = 200;

/** Push a received frame to `window.__SSE_FRAMES`, trimming to the cap. */
function recordDebugFrame(frame: SseMessage): void {
  if (typeof window === 'undefined') return;
  const buffer = (window.__SSE_FRAMES ??= []);
  buffer.push(frame);
  if (buffer.length > SSE_FRAMES_DEBUG_CAP) {
    buffer.splice(0, buffer.length - SSE_FRAMES_DEBUG_CAP);
  }
}

/** Async token accessor matching getTokenFromStore semantics. */
async function getToken(): Promise<string | null> {
  const token = tokenStore.getAccessToken();
  const expiresAt = tokenStore.getAccessTokenExpiresAt();
  if (!token) return null;
  if (expiresAt && Date.now() > expiresAt) {
    const ok = await refreshAccessToken();
    if (!ok) return null;
    return tokenStore.getAccessToken();
  }
  return token;
}

/**
 * Patch every cached `getOrders` result. RTK Query caches by serialized
 * query-args, so the list is filter/sort/page-specific. There's no single
 * key to target — we walk the cache state and patch each entry by name.
 */
function patchAllOrdersCaches(
  api: MiddlewareAPI,
  patch: (row: Record<string, unknown>) => void,
  orderId: string,
): void {
  const root = api.getState() as {
    ordersApi: { queries: Record<string, { endpointName?: string; originalArgs?: unknown }> };
  };
  const queries = root.ordersApi?.queries ?? {};
  for (const entry of Object.values(queries)) {
    if (entry?.endpointName !== 'getOrders') continue;
    dispatchCacheAction(
      api.dispatch,
      ordersApi.util.updateQueryData(
        'getOrders',
        entry.originalArgs as OrderListParams,
        (draft) => {
          const list = draft as { orders?: Array<Record<string, unknown> & { id: string }> };
          const row = list.orders?.find((o) => String(o.id) === String(orderId));
          if (row) patch(row);
        },
      ),
    );
  }
}

function applyEvent(api: MiddlewareAPI, event: SseMessage): void {
  const eventType = event?.EventType;
  const data = event?.Payload as
    | { OrderId?: string; NewStatus?: string; Source?: string; ExpiresAt?: string; OrderUpdatedAt?: string }
    | undefined;

  switch (eventType) {
    case 'orderUpdate': {
      const orderId = data?.OrderId;
      const status = data?.NewStatus;
      const eventTs = data?.OrderUpdatedAt;
      if (!orderId || !status) return;
      // Patch every cached getOrders result so the status chip updates
      // wherever the user is in the list (different page, different filters).
      // Guard: discard the event if it is older than what is already cached
      // (ISO 8601 strings compare lexicographically in chronological order).
      patchAllOrdersCaches(api, (row) => {
        if (eventTs && row.updatedAt && eventTs < (row.updatedAt as string)) return;
        row.status = status;
        if (eventTs) row.updatedAt = eventTs;
      }, orderId);
      // Patch the order-detail cache too.
      dispatchCacheAction(
        api.dispatch,
        ordersApi.util.updateQueryData('getOrderDetail', orderId, (draft) => {
          const detail = draft as { status?: string; updatedAt?: string };
          if (eventTs && detail.updatedAt && eventTs < detail.updatedAt) return;
          detail.status = status;
          if (eventTs) detail.updatedAt = eventTs;
        }),
      );
      // Trigger a fresh shipments + history fetch — the status flip may have
      // changed which actions are available and we want the next render to
      // reflect that without waiting for the user to navigate.
      dispatchCacheAction(
        api.dispatch,
        shipmentsApi.util.invalidateTags([{ type: 'ShipmentsByOrder', id: orderId }]),
      );
      dispatchCacheAction(
        api.dispatch,
        historyApi.util.invalidateTags([{ type: 'History', id: `Order:${orderId}` }]),
      );
      return;
    }

    case 'orderProcessingStarted': {
      const orderId = data?.OrderId;
      if (!orderId) return;
      const lock = { source: data?.Source, expiresAt: data?.ExpiresAt };
      patchAllOrdersCaches(api, (row) => { row.processingLock = lock; }, orderId);
      dispatchCacheAction(
        api.dispatch,
        ordersApi.util.updateQueryData('getOrderDetail', orderId, (draft) => {
          (draft as { processingLock?: unknown }).processingLock = lock;
        }),
      );
      // An order page re-read that started before the lock existed drops this patch when it lands.
      // RTK Query runs this invalidation only after that re-read, so the page reads the lock again.
      dispatchCacheAction(api.dispatch, ordersApi.util.invalidateTags([{ type: 'OrderDetail', id: orderId }]));
      markOrderProcessingStarted(orderId);
      return;
    }

    case 'orderProcessingCompleted': {
      const orderId = data?.OrderId;
      if (!orderId) return;
      patchAllOrdersCaches(api, (row) => { row.processingLock = null; }, orderId);
      dispatchCacheAction(
        api.dispatch,
        ordersApi.util.updateQueryData('getOrderDetail', orderId, (draft) => {
          (draft as { processingLock?: unknown }).processingLock = null;
        }),
      );
      // A run can change what the order page shows with no orderUpdate (an image editor save's
      // own run sends none), so an open page re-reads it; the orders list keeps the patch above.
      dispatchCacheAction(api.dispatch, ordersApi.util.invalidateTags([{ type: 'OrderDetail', id: orderId }]));
      dispatchCacheAction(api.dispatch, shipmentsApi.util.invalidateTags([{ type: 'ShipmentsByOrder', id: orderId }]));
      dispatchCacheAction(api.dispatch, historyApi.util.invalidateTags([{ type: 'History', id: `Order:${orderId}` }]));
      markOrderProcessingCompleted(orderId);
      return;
    }

    default:
      // Unknown event type — slice still buffers it so consumers can inspect.
      return;
  }
}

/** Open (or re-dial) the real SSE connection — this tab is leader (or solo).
 * Every dispatch is mirrored to follower tabs through the coordinator, which
 * no-ops when there is nothing to coordinate. */
function connectAsLeader(api: MiddlewareAPI): void {
  if (client) {
    client.setVerbose(currentVerbose);
  } else {
    client = new SseClient({
      baseUrl: services.sse,
      getToken,
      verbose: currentVerbose,
    });
    // Resume the stream cursor: prefer frames observed while following (a
    // promoted follower continues where the old leader stopped), else the
    // cursor preserved across this tab's own stop/start cycle.
    const resume = coordinator?.lastSeenEventId ?? preservedLastEventId;
    if (resume) client.lastEventId = resume;
    preservedLastEventId = null;
  }
  client.connect(
    (event) => {
      // QA convenience (legacy parity): mirror frames onto window when
      // verbose logging is enabled. Bounded + opt-in, so it's safe in prod.
      if (currentVerbose) recordDebugFrame(event);
      // Dispatch the envelope; the `sseEventReceived` branch below applies
      // the cache patches. One code path serves both the live transport
      // and the non-prod `__OM_SSE_PUSH` QA hook (which dispatches the same
      // action), so realtime patching is identical in tests and prod.
      api.dispatch(sseEventReceived(event));
      if (!api.getState().sse.connected) api.dispatch(sseConnected());
      coordinator?.broadcast({
        kind: 'frame',
        envelope: event,
        lastEventId: client?.lastEventId ?? null,
      });
    },
    (err) => {
      if (err.message?.includes('Auth Error')) {
        api.dispatch(sseAuthError(err.message));
        coordinator?.broadcast({ kind: 'status', status: 'authError', message: err.message });
      } else {
        // The only non-auth onError the client emits is the retries-
        // exhausted terminal failure — surface it as `off` so the status
        // chip can offer a manual reconnect.
        api.dispatch(sseRetriesExhausted());
        coordinator?.broadcast({ kind: 'status', status: 'off' });
      }
    },
    (status) => {
      if (status.type === 'open') {
        api.dispatch(sseConnected());
        coordinator?.broadcast({ kind: 'status', status: 'connected' });
      } else {
        api.dispatch(
          sseReconnecting({ attempt: status.attempt, maxRetries: status.maxRetries }),
        );
        coordinator?.broadcast({
          kind: 'status',
          status: 'reconnecting',
          attempt: status.attempt,
          maxRetries: status.maxRetries,
        });
      }
    },
  );
}

/** Follower-side: mirror the leader's broadcast into this tab's slice so the
 * cache patches and the status chip behave exactly as with a live transport. */
function applyRemoteMessage(
  api: MiddlewareAPI,
  msg: Extract<SseCoordMessage, { kind: 'frame' | 'status' }>,
): void {
  if (msg.kind === 'frame') {
    api.dispatch(sseEventReceived(msg.envelope));
    if (!api.getState().sse.connected) api.dispatch(sseConnected());
    return;
  }
  switch (msg.status) {
    case 'connected':
      api.dispatch(sseConnected());
      break;
    case 'reconnecting':
      api.dispatch(
        sseReconnecting(
          msg.attempt !== undefined && msg.maxRetries !== undefined
            ? { attempt: msg.attempt, maxRetries: msg.maxRetries }
            : undefined,
        ),
      );
      break;
    case 'off':
      api.dispatch(sseRetriesExhausted());
      break;
    case 'authError':
      api.dispatch(sseAuthError(msg.message));
      break;
  }
}

/** Leader-side: a new follower joined — re-broadcast the current status so it
 * doesn't sit in its optimistic "connecting" until the next transition. */
function rebroadcastStatus(api: MiddlewareAPI): void {
  const sse = (api.getState() as { sse: SseState }).sse;
  if (sse.authError) {
    coordinator?.broadcast({ kind: 'status', status: 'authError', message: sse.authError });
  } else if (sse.status === 'connected') {
    coordinator?.broadcast({ kind: 'status', status: 'connected' });
  } else if (sse.status === 'reconnecting') {
    coordinator?.broadcast(
      sse.retryAttempt !== null && sse.maxRetries !== null
        ? {
            kind: 'status',
            status: 'reconnecting',
            attempt: sse.retryAttempt,
            maxRetries: sse.maxRetries,
          }
        : { kind: 'status', status: 'reconnecting' },
    );
  } else if (sse.status === 'off') {
    coordinator?.broadcast({ kind: 'status', status: 'off' });
  }
  // `connecting` / `idle` need no broadcast — the follower's own optimistic
  // "connecting" from sseStart is already accurate while the leader dials.
}

export const sseMiddleware: Middleware =
  (api: MiddlewareAPI) => (next) => (action: unknown) => {
    const result = next(action);
    const a = action as { type?: string; payload?: { loggingEnabled?: boolean } };

    if (a.type === sseStart.type) {
      currentVerbose = a.payload?.loggingEnabled ?? false;
      if (coordinator) {
        // Repeat start on an already-coordinating tab (e.g. a same-page
        // remount): the connection owner re-dials as before; followers only
        // track the verbose preference.
        if (client) connectAsLeader(api);
      } else {
        coordinator = new SseTabCoordinator({
          onLead: () => connectAsLeader(api),
          onRemote: (msg) => applyRemoteMessage(api, msg),
          onReconnectRequest: () => client?.reconnectNow(),
          onHello: () => rebroadcastStatus(api),
        });
        coordinator.start();
      }
    } else if (a.type === sseReconnect.type) {
      // User asked for an immediate reconnect (status chip). Skips the
      // pending backoff timer; the connection owner resets its retry counter
      // and re-dials. Followers relay the request to the leader tab.
      if (client) client.reconnectNow();
      else coordinator?.requestReconnect();
      // Re-sync what the user is looking at. Last-Event-ID replay covers a
      // short outage, but events can age out server-side while updates were
      // off — refetching the active order list/detail queries closes that gap.
      // Blanket tag invalidation is acceptable here because it only runs on an
      // explicit user recovery action, never on the hot path.
      dispatchCacheAction(api.dispatch, ordersApi.util.invalidateTags(['Order', 'OrderList']));
    } else if (a.type === sseStop.type) {
      if (client) {
        preservedLastEventId = client.lastEventId;
        client.disconnect();
        client = null;
      } else if (coordinator?.lastSeenEventId) {
        // Follower leaving: keep the observed cursor in case this tab is the
        // next to start (and lead) again.
        preservedLastEventId = coordinator.lastSeenEventId;
      }
      coordinator?.stop();
      coordinator = null;
      api.dispatch(sseDisconnected());
    } else if (a.type === sseEventReceived.type) {
      // Apply cache patches for any received envelope — live or QA-injected.
      // Runs after `next(action)`, so the slice has already buffered/deduped it.
      applyEvent(api, (action as { payload: SseMessage }).payload);
    }

    return result;
  };
